import React, { useEffect, useRef, useState } from 'react';
import { Popover } from 'react-tiny-popover';
import { useFile } from 'components/useFile';
import { floor as floorName, trans } from 'components/langutils';
import { useData } from '../Data';

// The site plan and floor plans are SVGs uploaded in the admin (Building and
// Floor). Rooms in a floor plan have ids like "room118"; the current one gets
// the class iti-map__current (see iti.css, which also handles dark mode).

export function useStaffPlaces(user: any) {
  const { floors, buildings } = useData();
  const building = buildings.find((b: any) => b.name === user.building);
  const floor = floors.find((f: any) => f.building === user.building && f.level === user.level);
  return { building, floor };
}

export function MapImage({ src, highlight, label }: { src?: string, highlight?: string, label: string }) {
  const [svg] = useFile(src, (r) => r.text());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = ref.current;
    if (!box || !svg) {
      return;
    }
    box.innerHTML = svg;
    const image = box.querySelector('svg');
    if (!image) {
      return;
    }
    // scale with the container instead of the size saved by the drawing program
    image.removeAttribute('width');
    image.removeAttribute('height');
    image.setAttribute('role', 'img');
    image.setAttribute('aria-label', label);
    if (highlight) {
      image.querySelector(`#${CSS.escape(highlight)}`)?.classList.add('iti-map__current');
    }
  }, [svg, highlight, label]);

  return <div className="iti-map" ref={ref} />;
}

const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Opens on tap or click everywhere, and additionally on hover where there is a mouse.
export function MapButton({ label, map, text, className = 'iti-map-trigger' }: { label: string, map: React.ReactNode, text: React.ReactNode, className?: string }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;

  // the popover lives in <body>; its z-index keeps it above the table and the sticky staff bar
  return (
    <Popover
      isOpen={open}
      positions={['bottom', 'top', 'left', 'right']}
      align="end"
      padding={6}
      containerStyle={{ zIndex: '1100' }}
      onClickOutside={() => { setPinned(false); setHovered(false); }}
      content={<div className="iti-map-popover" role="dialog" aria-label={label}>{map}</div>}
    >
      <button
        type="button"
        className={className}
        title={label}
        aria-label={typeof text === 'string' ? `${text}: ${label}` : label}
        aria-expanded={open}
        onClick={() => setPinned(!pinned)}
        onMouseEnter={() => canHover() && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onKeyDown={(event) => { if (event.key === 'Escape') { setPinned(false); setHovered(false); } }}
      >
        {text}
      </button>
    </Popover>
  );
}

type LocationProps = { lang: 'de' | 'en', floorImage?: string, buildingImage?: string, room?: string, building?: string };

// Both maps for a person's page: small next to the address, a tap or click shows them large.
// Needs nothing but its properties, so the <staff-location> element can use it on a page from the server.
export function LocationMaps({ lang, floorImage, buildingImage, room, building }: LocationProps) {
  const t = trans(lang);
  if (!buildingImage && !floorImage) {
    return null;
  }
  const floorLabel = `${t.floor_plan}: ${t.room} ${room}`;
  const siteLabel = `${t.site_plan}: ${t.building} ${building}`;
  return (
    <section className="iti-location iti-location--compact" aria-label={t.location}>
      <h2>{t.location}</h2>
      <div className="iti-location__maps">
        {floorImage && <figure>
          <MapButton className="iti-location__open" label={floorLabel} text={<MapImage src={floorImage} highlight={room && `room${room}`} label={floorLabel} />}
            map={<MapImage src={floorImage} highlight={room && `room${room}`} label={floorLabel} />} />
          <figcaption>{t.floor_plan}{room && <>, {t.room} {room}</>}</figcaption>
        </figure>}
        {buildingImage && <figure>
          <MapButton className="iti-location__open" label={siteLabel} text={<MapImage src={buildingImage} label={siteLabel} />}
            map={<MapImage src={buildingImage} label={siteLabel} />} />
          <figcaption>{t.site_plan}, {t.building} {building}</figcaption>
        </figure>}
      </div>
    </section>
  );
}

export function StaffLocation({ user }: { user: any }) {
  const { lang } = useData();
  const { building, floor } = useStaffPlaces(user);
  return <LocationMaps lang={lang} floorImage={floor?.image} buildingImage={building?.image} room={user.room} building={user.building} />;
}
